
import BTThemeProvider from "@common-components/bt-theme-provider";
import TaskView from "@common-views/task-view/task-view";
import store from "@extension-context-common/store/extension-store";
import { Provider } from "react-redux";
import { useEffect, useRef, useState } from "react";
import type { ComponentProps } from "react";
import { Droppable as ReactBeautifulDroppable } from "react-beautiful-dnd";
import { IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { btLayoutSidebarActions } from "@common-reducers/BtLayoutSidebarReducer";
import { taskActions } from "@common-reducers/TasksReducer";
import { getWaContactDisplayLabel } from "@common-reducers/WhatsAppSelectors";
import { setSelectedBoardId } from "@common-reducers/BoardsThunks";
import { subscribeCurrentBoardToSocket } from "@extension/context/content/thunks/WhatsappThunks";
import { ShadowDOMThemeProvider } from "../shadow-dom-theme-provider";
import { boardsSelectByQuery, DBBoardsThunks } from "@common-reducers/DBServiceThunks";

// =================================================================================================
// DnD Bridge Logic (Extracted from tasks.injection.tsx)
// =================================================================================================

const BRIDGED_EVENT_TYPES = new Set<string>([
    "pointerdown",
    "pointermove",
    "pointerup",
    "pointercancel",
    "mousedown",
    "mousemove",
    "mouseup"
]);

type ListenerEntry = {
    wrapped: EventListener;
    options?: boolean | AddEventListenerOptions;
};

type ListenerRegistry = Map<EventListenerOrEventListenerObject, Map<string, ListenerEntry>>;

const bridgeRegistry = new WeakMap<ShadowRoot, { count: number; teardown: () => void }>();

type DroppableDefaultProps = {
    getContainerForClone?: () => Element | DocumentFragment;
};

const droppableDefaultProps: DroppableDefaultProps | undefined =
    (ReactBeautifulDroppable as unknown as { defaultProps?: DroppableDefaultProps }).defaultProps;

let originalGetContainerForClone: (() => Element | DocumentFragment) | undefined;
let activeClonePortal: Element | DocumentFragment | null = null;

const overrideGetContainerForClone = (): Element | DocumentFragment => {
    if (activeClonePortal) {
        return activeClonePortal;
    }

    if (typeof originalGetContainerForClone === "function") {
        return originalGetContainerForClone();
    }

    if (typeof document !== "undefined" && document.body) {
        return document.body;
    }

    throw new Error("Unable to resolve container for react-beautiful-dnd clone");
};

const ensurePortalContainer = (shadowRoot: ShadowRoot): HTMLElement => {
    let portal = shadowRoot.querySelector<HTMLElement>("[data-bt-rbd-portal='true']");
    if (!portal) {
        const ownerDocument = shadowRoot.ownerDocument ?? document;
        portal = ownerDocument.createElement("div");
        portal.setAttribute("data-bt-rbd-portal", "true");
        portal.style.position = "relative";
        shadowRoot.appendChild(portal);
    }

    return portal;
};

const getHandleFromEvent = (event: Event, contextShadowRoot?: ShadowRoot): Element | null => {
    if (typeof (event as any).composedPath === "function") {
        const path = (event as any).composedPath() as EventTarget[];
        const pathHasContextRoot = contextShadowRoot ? path.includes(contextShadowRoot) : true;
        if (!pathHasContextRoot) {
            return null;
        }

        for (const item of path) {
            if (item instanceof Element && item.hasAttribute("data-rbd-drag-handle-context-id")) {
                return item;
            }
        }
    }

    const target = event.target;
    if (target instanceof Element) {
        const targetRoot = target.getRootNode();
        if (!contextShadowRoot || targetRoot === contextShadowRoot) {
            return target;
        }
    }

    return null;
};

const cloneEventForTarget = (event: Event, target: Element, currentTarget: EventTarget): Event => {
    const baseInit = {
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        composed: (event as any).composed ?? true
    };

    let cloned: Event;

    if (typeof PointerEvent !== "undefined" && event instanceof PointerEvent) {
        const init: PointerEventInit = {
            ...baseInit,
            pointerId: event.pointerId,
            width: event.width,
            height: event.height,
            pressure: event.pressure,
            tangentialPressure: event.tangentialPressure,
            tiltX: event.tiltX,
            tiltY: event.tiltY,
            twist: event.twist,
            pointerType: event.pointerType,
            isPrimary: event.isPrimary,
            clientX: event.clientX,
            clientY: event.clientY,
            screenX: event.screenX,
            screenY: event.screenY,
            button: event.button,
            buttons: event.buttons,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            metaKey: event.metaKey,
            relatedTarget: event.relatedTarget
        };
        cloned = new PointerEvent(event.type, init);
    } else if (event instanceof MouseEvent) {
        const init: MouseEventInit = {
            ...baseInit,
            clientX: event.clientX,
            clientY: event.clientY,
            screenX: event.screenX,
            screenY: event.screenY,
            button: event.button,
            buttons: event.buttons,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            metaKey: event.metaKey,
            relatedTarget: event.relatedTarget,
            detail: event.detail
        };
        cloned = new MouseEvent(event.type, init);
    } else {
        cloned = new Event(event.type, baseInit);
    }

    const composedPath =
        typeof (event as any).composedPath === "function"
            ? () => (event as any).composedPath()
            : () => (target ? [target] : []);

    const define = (prop: PropertyKey, descriptor: PropertyDescriptor) => {
        Object.defineProperty(cloned, prop, {
            ...descriptor,
            configurable: true
        });
    };

    define("target", { get: () => target });
    define("srcElement", { get: () => target });
    define("currentTarget", { get: () => currentTarget });
    define("composedPath", { value: composedPath });
    define("preventDefault", {
        value: () => {
            event.preventDefault();
        }
    });
    define("stopPropagation", {
        value: () => {
            event.stopPropagation();
        }
    });
    define("stopImmediatePropagation", {
        value: () => {
            event.stopImmediatePropagation();
        }
    });
    define("defaultPrevented", { get: () => event.defaultPrevented });
    define("timeStamp", { get: () => event.timeStamp });

    return cloned;
};

const callListener = (
    listener: EventListenerOrEventListenerObject,
    context: EventTarget,
    bridgedEvent: Event
) => {
    if (typeof listener === "function") {
        listener.call(context, bridgedEvent);
    } else if (listener && typeof (listener as EventListenerObject).handleEvent === "function") {
        (listener as EventListenerObject).handleEvent.call(listener, bridgedEvent);
    }
};

const installShadowDnDBridge = (shadowRoot: ShadowRoot): (() => void) => {
    const originalDocumentAdd = document.addEventListener.bind(document);
    const originalDocumentRemove = document.removeEventListener.bind(document);
    const originalWindowAdd = window.addEventListener.bind(window);
    const originalWindowRemove = window.removeEventListener.bind(window);

    const documentRegistry: ListenerRegistry = new Map();
    const windowRegistry: ListenerRegistry = new Map();

    const createPatchedMethods = (
        originalAdd: typeof document.addEventListener,
        originalRemove: typeof document.removeEventListener,
        registry: ListenerRegistry
    ) => {
        const patchedAdd: typeof document.addEventListener = function (
            type: string,
            listener: EventListenerOrEventListenerObject | null,
            options?: boolean | AddEventListenerOptions
        ) {
            if (
                typeof type !== "string" ||
                !listener ||
                !BRIDGED_EVENT_TYPES.has(type)
            ) {
                return originalAdd(type, listener as any, options);
            }

            let typeMap = registry.get(listener);
            if (!typeMap) {
                typeMap = new Map();
                registry.set(listener, typeMap);
            }

            if (typeMap.has(type)) {
                return;
            }

            const wrapped: EventListener = function (this: EventTarget, event: Event) {
                const handle = getHandleFromEvent(event, shadowRoot);
                if (!handle) {
                    callListener(listener, this, event);
                    return;
                }

                const bridged = cloneEventForTarget(event, handle, this);
                callListener(listener, this, bridged);
            };

            typeMap.set(type, { wrapped, options });
            return originalAdd(type, wrapped, options);
        };

        const patchedRemove: typeof document.removeEventListener = function (
            type: string,
            listener: EventListenerOrEventListenerObject | null,
            options?: boolean | EventListenerOptions
        ) {
            if (
                typeof type !== "string" ||
                !listener ||
                !BRIDGED_EVENT_TYPES.has(type)
            ) {
                return originalRemove(type, listener as any, options);
            }

            const typeMap = registry.get(listener);
            if (typeMap) {
                const entry = typeMap.get(type);
                if (entry) {
                    originalRemove(type, entry.wrapped as any, entry.options);
                    typeMap.delete(type);
                }

                if (typeMap.size === 0) {
                    registry.delete(listener);
                }
                return;
            }

            return originalRemove(type, listener as any, options);
        };

        return { patchedAdd, patchedRemove };
    };

    const docPatched = createPatchedMethods(
        originalDocumentAdd,
        originalDocumentRemove,
        documentRegistry
    );
    const winPatched = createPatchedMethods(
        originalWindowAdd,
        originalWindowRemove,
        windowRegistry
    );

    const originalElementFromPoint = document.elementFromPoint.bind(document);
    const originalElementsFromPoint = document.elementsFromPoint.bind(document);
    const originalQuerySelector = document.querySelector.bind(document);
    const originalQuerySelectorAll = document.querySelectorAll.bind(document);
    const originalGetElementById = document.getElementById.bind(document);

    const preferShadowElement = (element: Element | null) => {
        if (!element) {
            return null;
        }

        if (element === shadowRoot.host || shadowRoot.contains(element)) {
            return element;
        }

        if (element.shadowRoot && element.shadowRoot !== shadowRoot) {
            const inner = element.shadowRoot.elementFromPoint?.(0, 0);
            if (inner) {
                return inner;
            }
        }

        return element;
    };

    document.addEventListener = docPatched.patchedAdd;
    document.removeEventListener = docPatched.patchedRemove;
    window.addEventListener = winPatched.patchedAdd;
    window.removeEventListener = winPatched.patchedRemove;

    document.elementFromPoint = function (x: number, y: number): Element | null {
        const fromShadow = shadowRoot.elementFromPoint(x, y);
        if (fromShadow) {
            return fromShadow;
        }

        return preferShadowElement(originalElementFromPoint(x, y));
    };

    document.elementsFromPoint = function (x: number, y: number): Element[] {
        const originals = originalElementsFromPoint(x, y);
        const results: Element[] = [];
        const seen = new Set<Element>();

        for (const element of originals) {
            const prefered = preferShadowElement(element);
            if (prefered && !seen.has(prefered)) {
                seen.add(prefered);
                results.push(prefered);
            }
        }

        const shadowElements = shadowRoot.elementsFromPoint(x, y);
        for (const el of shadowElements) {
            if (!seen.has(el)) {
                seen.add(el);
                results.push(el);
            }
        }

        return results;
    };

    document.querySelector = function (selectors: string): Element | null {
        const existing = originalQuerySelector(selectors);
        if (existing) {
            return existing;
        }

        return shadowRoot.querySelector(selectors);
    };

    document.querySelectorAll = function (selectors: string): NodeListOf<Element> {
        const existing = originalQuerySelectorAll(selectors);
        if (existing.length > 0) {
            return existing;
        }

        return shadowRoot.querySelectorAll(selectors);
    } as typeof document.querySelectorAll;

    document.getElementById = function (id: string): HTMLElement | null {
        const existing = originalGetElementById(id);
        if (existing) {
            return existing;
        }

        if (!id) {
            return existing;
        }

        // CSS.escape polyfill/check might be needed but assuming modern browser or simplified for now.
        // tasks.injection.tsx used:
        const selector = typeof CSS !== "undefined" && typeof CSS.escape === "function"
             ? `#${CSS.escape(id)}`
             : `[id="${id}"]`;

        return shadowRoot.querySelector(selector) as HTMLElement | null;
    };

    return () => {
        document.addEventListener = originalDocumentAdd;
        document.removeEventListener = originalDocumentRemove;
        window.addEventListener = originalWindowAdd;
        window.removeEventListener = originalWindowRemove;
        document.elementFromPoint = originalElementFromPoint;
        document.elementsFromPoint = originalElementsFromPoint;
        document.querySelector = originalQuerySelector;
        document.querySelectorAll = originalQuerySelectorAll;
        document.getElementById = originalGetElementById;

        documentRegistry.forEach((typeMap) => {
            typeMap.forEach(({ wrapped, options }, type) => {
                originalDocumentRemove(type, wrapped as any, options);
            });
        });
        documentRegistry.clear();

        windowRegistry.forEach((typeMap) => {
            typeMap.forEach(({ wrapped, options }, type) => {
                originalWindowRemove(type, wrapped as any, options);
            });
        });
        windowRegistry.clear();
    };
};

const ensureShadowDnDBridge = (shadowRoot: ShadowRoot): (() => void) => {
    let entry = bridgeRegistry.get(shadowRoot);
    if (!entry) {
        entry = {
            count: 0,
            teardown: installShadowDnDBridge(shadowRoot)
        };
        bridgeRegistry.set(shadowRoot, entry);
    }

    entry.count += 1;

    return () => {
        entry!.count -= 1;
        if (entry!.count <= 0) {
            entry!.teardown();
            bridgeRegistry.delete(shadowRoot);
        }
    };
};


const TaskViewWithShadowBridge = (
    props: ComponentProps<typeof TaskView> & { shadowRoot?: ShadowRoot | null }
) => {
    const cleanupRef = useRef<(() => void) | null>(null);
    const currentShadowRootRef = useRef<ShadowRoot | null>(null);
    const portalRef = useRef<HTMLElement | null>(null);

    const activeDnDShadowRoot = props.shadowRoot || null;

    if (activeDnDShadowRoot && currentShadowRootRef.current !== activeDnDShadowRoot) {
        cleanupRef.current?.();
        cleanupRef.current = ensureShadowDnDBridge(activeDnDShadowRoot);
        currentShadowRootRef.current = activeDnDShadowRoot;
    }

    useEffect(() => {
        // Inject DnD styles and setup portal
        if(activeDnDShadowRoot) {
            const style = document.createElement('style');
             style.textContent = `
                [data-rbd-drag-handle-context-id] {
                    cursor: grab !important;
                    pointer-events: auto !important;
                }
                [data-rbd-drag-handle-context-id]:active {
                    cursor: grabbing !important;
                }
            `;
            activeDnDShadowRoot.appendChild(style);

            // Setup portal for drag clone
            const portal = ensurePortalContainer(activeDnDShadowRoot);
            activeClonePortal = portal;

            // Patch Droppable default props if not already matched
            const DroppableAny = ReactBeautifulDroppable as any;
            if (!DroppableAny.defaultProps) DroppableAny.defaultProps = {};
            if (DroppableAny.defaultProps.getContainerForClone !== overrideGetContainerForClone) {
                 originalGetContainerForClone = DroppableAny.defaultProps.getContainerForClone;
                 DroppableAny.defaultProps.getContainerForClone = overrideGetContainerForClone;
            }

            return () => { 
                style.remove();
                if (activeClonePortal === portal) {
                    activeClonePortal = null;
                }
            }
        }
    }, [activeDnDShadowRoot]);

    useEffect(() => {
        return () => {
            cleanupRef.current?.();
        };
    }, []);

    return <TaskView {...props} />;
};

interface TasksSideBarWrapperProps {
    hideHeader?: boolean;
}

export default function TasksSideBarWrapper({ hideHeader = false }: TasksSideBarWrapperProps) {
    const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if(ref.current) {
            // Find the closest shadow root
            const root = ref.current.getRootNode();
            if(root instanceof ShadowRoot) {
                setShadowRoot(root);
            }
        }
    }, []);

    const dispatch = useAppDispatch();
    const currentChat = useAppSelector(state => state.WhatsAppReducer.currentChat);
    const currentChatId = currentChat?.id?._serialized;
    const currentSelectedBoardId = useAppSelector(state => state.BoardsReducer.selectedBoardId);

    // Find the board for the current chat
    const boardForCurrentChat = useAppSelector(state => {
        const boardList = boardsSelectByQuery(state.DBBoardsReducer, {});
        return boardList.find(b =>
            b._IdByWaChatIds?.includes(currentChatId ?? '') && b.isWABoard
        );
    });

    // Track which chatIds we've already tried to fetch to avoid repeated fetches
    const fetchedChatIdsRef = useRef<Set<string>>(new Set());

    // When chat changes, update the selected board if needed, or fetch if not in store
    useEffect(() => {
        if (!currentChatId) return;

        if (boardForCurrentChat) {
            // Board exists locally - select it if not already selected
            if (boardForCurrentChat._id !== currentSelectedBoardId) {
                console.log("[board-id] TasksSideBarWrapper: Chat changed, selecting board:", boardForCurrentChat._id, "for chat:", currentChatId);
                dispatch(setSelectedBoardId({ boardId: boardForCurrentChat._id ?? '' }));
                dispatch(subscribeCurrentBoardToSocket());
            }
        } else {
            // No board found locally - try to fetch from server if we haven't already
            if (!fetchedChatIdsRef.current.has(currentChatId)) {
                fetchedChatIdsRef.current.add(currentChatId);
                console.log("[board-id] TasksSideBarWrapper: No local board for chat, fetching from server:", currentChatId);

                dispatch(DBBoardsThunks.find({
                    _IdByWaChatIds: currentChatId,
                    $limit: 1
                })).then((res: any) => {
                    console.log("[board-id] TasksSideBarWrapper: Fetch result:", res);

                    // Check if board was returned in the response
                    if (res?.payload?.data && res.payload.data.length > 0) {
                        const board = res.payload.data[0];
                        console.log("[board-id] TasksSideBarWrapper: Fetched board:", board._id);
                        dispatch(setSelectedBoardId({ boardId: board._id }));
                        dispatch(subscribeCurrentBoardToSocket());
                    }
                });
            }
        }
    }, [currentChatId, boardForCurrentChat?._id, currentSelectedBoardId, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(taskActions.setTasksFilterName(''));
        };
    }, [dispatch]);
    
    return (
        <div ref={ref} className="h-full flex flex-col">
            <BTThemeProvider>
                {!hideHeader && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        borderBottom: '1px solid var(--border-color, #e0e0e0)',
                        backgroundColor: 'var(--color-surface, #fff)',
                        flexShrink: 0
                    }}>
                        <IconButton size="small" onClick={() => dispatch(btLayoutSidebarActions.closeSidebar())}>
                            <CloseIcon />
                        </IconButton>
                        <div style={{ fontWeight: 500, fontSize: '16px' }}>
                            Tasks with {getWaContactDisplayLabel(currentChat)}
                        </div>
                        {/* Placeholder for title or other actions if needed */}
                        <div />
                    </div>
                )}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    {shadowRoot && (
                        <ShadowDOMThemeProvider shadowRoot={shadowRoot}>
                            <TaskViewWithShadowBridge shadowRoot={shadowRoot} isDisplayTasksHeader={true} />
                        </ShadowDOMThemeProvider>
                    )}
                </div>
            </BTThemeProvider>
        </div>
    );
}
